import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CloudUpload, ShieldCheck, AlertCircle } from 'lucide-react';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS } from '../../styles/emziCI';
import { secureStorage } from '../../utils/secureStorage';
import { BACKEND_URL } from '../../utils/backendUrl';

const BackupMaintenanceCard = () => {
  const t = useLang();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchStatus();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchStatus();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/backup/status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error fetching backup status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManualBackup = async () => {
    if (!window.confirm(t('admin.backup.confirmManual'))) return;

    setProcessing(true);
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/backup/manual`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ reason: 'Manual backup triggered from admin panel' })
        }
      );

      if (response.ok) {
        alert(`✅ ${t('admin.backup.backupComplete')}\n\n${t('admin.audit.logged')}`);
        fetchStatus();
      } else {
        alert(`❌ ${t('admin.backup.backupFailed')}`);
      }
    } catch (error) {
      alert(`❌ ${t('admin.error.networkError')}`);
    } finally {
      setProcessing(false);
    }
  };

  const toggleMaintenance = async () => {
    const enabled = !status?.maintenance_mode;
    const message = enabled 
      ? t('admin.maintenance.confirmEnable')
      : t('admin.maintenance.confirmDisable');
    
    if (!window.confirm(message)) return;

    setProcessing(true);
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/maintenance/toggle`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            enabled, 
            reason: 'Toggled from admin panel',
            estimated_duration_minutes: 15
          })
        }
      );

      if (response.ok) {
        alert(`✅ ${t('admin.maintenance.updated')}\n\n${t('admin.audit.logged')}`);
        fetchStatus();
      }
    } catch (error) {
      alert(`❌ ${t('admin.error.networkError')}`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card 
      className="hover:shadow-lg transition-shadow border-0"
      style={{
        boxShadow: EMZI_SHADOWS.card,
        borderRadius: EMZI_RADIUS.md,
      }}
    >
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <CloudUpload size={24} strokeWidth={2} style={{ color: EMZI_COLORS.success }} />
          <div>
            <div style={{ color: EMZI_COLORS.primary, fontWeight: '700' }}>
              {t('admin.backup.title')}
            </div>
            <div className="text-xs font-normal text-gray-600 mt-1">
              {t('admin.backup.subtitle')}
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Status Cards */}
        <div className="grid grid-cols-2 gap-2">
          {/* ISO Compliance */}
          <div 
            className="rounded-lg p-3"
            style={{
              backgroundColor: status?.iso_compliance_active ? '#F0FDF4' : '#FEF2F2',
              border: status?.iso_compliance_active 
                ? `1px solid ${EMZI_COLORS.success}20`
                : `1px solid ${EMZI_COLORS.error}20`,
              borderRadius: EMZI_RADIUS.sm,
            }}
          >
            <div 
              className="text-xs mb-1"
              style={{ color: EMZI_COLORS.textSecondary }}
            >
              {t('admin.backup.isoCompliance')}
            </div>
            <div 
              className="text-lg font-bold flex items-center gap-1"
              style={{ 
                color: status?.iso_compliance_active ? EMZI_COLORS.success : EMZI_COLORS.error 
              }}
            >
              {status?.iso_compliance_active ? (
                <>
                  <ShieldCheck size={18} strokeWidth={2} />
                  <span>Active</span>
                </>
              ) : (
                <>
                  <AlertCircle size={18} strokeWidth={2} />
                  <span>Inactive</span>
                </>
              )}
            </div>
          </div>

          {/* Maintenance Mode */}
          <div 
            className="rounded-lg p-3"
            style={{
              backgroundColor: status?.maintenance_mode ? `${EMZI_COLORS.accent}15` : '#F3F4F6',
              border: status?.maintenance_mode 
                ? `1px solid ${EMZI_COLORS.accent}30`
                : `1px solid ${EMZI_COLORS.border}`,
              borderRadius: EMZI_RADIUS.sm,
            }}
          >
            <div 
              className="text-xs mb-1"
              style={{ color: EMZI_COLORS.textSecondary }}
            >
              {t('admin.maintenance.mode')}
            </div>
            <div 
              className="text-lg font-bold"
              style={{ 
                color: status?.maintenance_mode ? EMZI_COLORS.accent : EMZI_COLORS.textSecondary 
              }}
            >
              {status?.maintenance_mode ? '🚧 ON' : '○ OFF'}
            </div>
          </div>
        </div>

        {/* Last Backup Info */}
        <div 
          className="rounded-lg p-3"
          style={{
            backgroundColor: '#EBF4FF',
            border: `1px solid ${EMZI_COLORS.primary}20`,
            borderRadius: EMZI_RADIUS.sm,
          }}
        >
          <div 
            className="text-xs mb-1"
            style={{ color: EMZI_COLORS.textSecondary }}
          >
            {t('admin.backup.lastBackup')}
          </div>
          <div 
            className="text-sm font-semibold"
            style={{ color: EMZI_COLORS.primary }}
          >
            {status?.last_backup_at ? (
              new Date(status.last_backup_at).toLocaleString()
            ) : (
              t('admin.backup.noBackup')
            )}
          </div>
          {status?.last_backup_size_mb && (
            <div 
              className="text-xs mt-1"
              style={{ color: EMZI_COLORS.textSecondary }}
            >
              Size: {status.last_backup_size_mb} MB
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <button
            onClick={handleManualBackup}
            disabled={processing}
            className="w-full py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 hover:opacity-90"
            style={{
              backgroundColor: EMZI_COLORS.success,
              color: '#FFFFFF',
              borderRadius: EMZI_RADIUS.sm,
              minHeight: '44px',
            }}
          >
            {processing ? (
              <>
                <span className="animate-spin">⏳</span>
                {t('common.processing')}...
              </>
            ) : (
              <>
                <CloudUpload size={16} strokeWidth={2} />
                {t('admin.backup.backupNow')}
              </>
            )}
          </button>

          <button
            onClick={toggleMaintenance}
            disabled={processing}
            className="w-full py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 hover:opacity-90"
            style={{
              backgroundColor: status?.maintenance_mode ? EMZI_COLORS.textSecondary : EMZI_COLORS.accent,
              color: '#FFFFFF',
              borderRadius: EMZI_RADIUS.sm,
              minHeight: '44px',
            }}
          >
            {status?.maintenance_mode ? (
              <>○ {t('admin.maintenance.disable')}</>
            ) : (
              <>🚧 {t('admin.maintenance.enable')}</>
            )}
          </button>
        </div>

        {/* Auto-refresh indicator */}
        <div 
          className="text-xs text-center"
          style={{ color: EMZI_COLORS.textMuted }}
        >
          🔄 {t('admin.backup.autoRefresh')}
        </div>
      </CardContent>
    </Card>
  );
};

export default BackupMaintenanceCard;